import { ChevronRight, Home } from "lucide-react";
import Text from "./Text";
import { ROUTE_PATHS } from "../../../constants/routeConstants";

const PageHero = ({
  title,
  crumbs = [],
  eyebrow,
  description,
  align = "center",
}) => {
  const alignClass =
    align === "left"
      ? "items-start text-left"
      : "items-center text-center";

  const homeAlreadyInCrumbs = crumbs.some(
    (c) => c.href === ROUTE_PATHS.HOME || c.label?.toLowerCase() === "home",
  );

  return (
    <section className="relative w-full overflow-hidden border-b border-gray-100 bg-white">
      {/* Decorative backdrop */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
      >
        {/* Soft radial wash */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_80%_at_50%_0%,rgba(59,130,246,0.08),transparent_70%)]" />
        {/* Bottom fade */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-white to-transparent" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #000 1px, transparent 1px), linear-gradient(to bottom, #000 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      {/* Giant ghost monogram (first letter of title) */}
      {title && (
        <div
          aria-hidden="true"
          className="hidden md:flex absolute inset-0 items-center justify-center pointer-events-none select-none"
        >
          <span className="font-serif text-[18rem] leading-none font-bold text-gray-900/[0.025] tracking-tighter">
            {title.charAt(0).toUpperCase()}
          </span>
        </div>
      )}

      <div
        className={`relative max-w-[1440px] mx-auto px-6 lg:px-8 py-16 md:py-20 lg:py-24 flex flex-col ${alignClass} gap-4`}
      >
        {/* Eyebrow */}
        {eyebrow && (
          <Text
            is="span"
            className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-blue-600"
          >
            <span className="w-6 h-px bg-blue-600" />
            {eyebrow}
            <span className="w-6 h-px bg-blue-600" />
          </Text>
        )}

        {/* Title */}
        <Text
          is="h1"
          className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-gray-900 leading-tight tracking-tight max-w-3xl"
        >
          {title}
        </Text>

        {/* Description */}
        {description && (
          <Text
            is="p"
            className="text-sm md:text-base text-gray-500 leading-relaxed max-w-xl"
          >
            {description}
          </Text>
        )}

        {/* Breadcrumbs */}
        <nav
          aria-label="Breadcrumb"
          className={`mt-2 flex items-center flex-wrap gap-x-2 gap-y-1 text-xs ${align === "left" ? "justify-start" : "justify-center"}`}
        >
          {!homeAlreadyInCrumbs && (
            <>
              <Text
                is="a"
                href={ROUTE_PATHS.HOME}
                className="inline-flex items-center gap-1 text-gray-500 hover:text-blue-600 transition-colors"
              >
                <Home size={11} />
                Home
              </Text>
              {crumbs.length > 0 && (
                <ChevronRight size={11} className="text-gray-300" />
              )}
            </>
          )}
          {crumbs.map((crumb, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <span key={i} className="inline-flex items-center gap-2">
                {crumb.href && !isLast ? (
                  <Text
                    is="a"
                    href={crumb.href}
                    className="text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    {crumb.label}
                  </Text>
                ) : (
                  <span
                    className={
                      isLast
                        ? "text-gray-900 font-medium"
                        : "text-gray-500"
                    }
                    aria-current={isLast ? "page" : undefined}
                  >
                    {crumb.label}
                  </span>
                )}
                {!isLast && (
                  <ChevronRight size={11} className="text-gray-300" />
                )}
              </span>
            );
          })}
        </nav>
      </div>
    </section>
  );
};

export default PageHero;
